// frontend/src/utils/projectLibrary.ts
import { Project } from '../types';

export const projectLibrary: Project[] = [
  {
    trigger: 'analysis',
    folderName: 'Sentience_Analytics',
    files: [
      {
        name: 'main.py',
        language: 'python',
        script: `import time
from data_loader import stream_data
from visuals import render_graph

print("Initializing Analytics Engine...")
time.sleep(1)

# Connect to stream
data_stream = stream_data(source="cortex_v9")

while True:
    packet = next(data_stream)
    if packet['anomaly_score'] > 0.9:
        print(f"CRITICAL: Anomaly detected in vector {packet['id']}")
        render_graph(packet)
    time.sleep(0.1)`
      },
      {
        name: 'data_loader.py',
        language: 'python',
        script: `import random

def stream_data(source):
    print(f"Connecting to {source}...")
    while True:
        yield {
            'id': random.randint(1000, 9999),
            'value': random.random(),
            'anomaly_score': random.random()
        }`
      },
      {
        name: 'visuals.py',
        language: 'python',
        script: `def render_graph(data):
    # Mocking a render call
    print(f"[GRAPH] Rendering node {data['id']} at intensity {data['value']:.2f}")`
      }
    ]
  },
  {
    trigger: 'entities',
    folderName: 'Entity_Tracker',
    files: [
      {
        name: 'index.html',
        language: 'html',
        script: `<!DOCTYPE html>
<html>
<head>
    <title>Entity Tracking Grid</title>
    <link rel="stylesheet" href="style.css">
</head>
<body>
    <div id="grid"></div>
    <script src="tracker.js"></script>
</body>
</html>`
      },
      {
        name: 'style.css',
        language: 'css',
        script: `body { background: #000; color: #0f0; }
#grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.entity { border: 1px solid #0f0; padding: 20px; }`
      },
      {
        name: 'tracker.js',
        language: 'javascript',
        script: `const grid = document.getElementById('grid');
console.log("Tracking active...");
setInterval(() => {
    const el = document.createElement('div');
    el.className = 'entity';
    el.innerText = 'TARGET-' + Math.floor(Math.random() * 999);
    grid.appendChild(el);
}, 1000);`
      }
    ]
  },
  // --- NEW PROJECT: STORY & WORLDBUILDING ENGINE ---
  {
    trigger: 'story',
    folderName: 'Omniverse_Engine',
    files: [
      {
        name: 'index.html',
        language: 'html',
        script: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>Omniverse Dashboard</title>
    <style>
        body { background: #1a1a1a; color: #e0e0e0; font-family: sans-serif; padding: 20px; }
        .dashboard { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
        .card { background: #2d2d2d; padding: 20px; border-radius: 8px; border: 1px solid #444; }
        h1 { color: #9cdcfe; }
        h3 { color: #dcdcaa; margin-top: 0; }
        .status { color: #4ec9b0; font-size: 0.9em; }
    </style>
</head>
<body>
    <h1>Omniverse Narrative Engine</h1>
    <div class="dashboard">
        <div class="card">
            <h3>Character DB</h3>
            <p>Active Entities: 142</p>
            <p class="status">● All arcs consistent</p>
        </div>
        <div class="card">
            <h3>Lore Matrix</h3>
            <p>World Facts: 4,096</p>
            <p class="status">● No contradictions found</p>
        </div>
        <div class="card">
            <h3>Timeline</h3>
            <p>Current Era: Post-Collapse</p>
            <p class="status">● Event flow linear</p>
        </div>
        <div class="card">
             <h3>Drafting</h3>
             <p>Chapter 4 in progress...</p>
        </div>
    </div>
</body>
</html>`
      },
      {
        name: 'lore_engine.py',
        language: 'python',
        script: `class LoreDatabase:
    def __init__(self):
        self.facts = {}
        self.relations = []

    def add_fact(self, key, value, era="Ancient"):
        print(f"[LORE] Indexing fact: {key} ({era})")
        self.facts[key] = value

    def query(self, topic):
        if topic in self.facts:
            return self.facts[topic]
        return "Unknown entity."

world = LoreDatabase()
world.add_fact("The Great Fall", "Year 2042", era="Modern")
world.add_fact("Magic System", "Ether-based", era="Ancient")`
      },
      {
        name: 'continuity_checker.py',
        language: 'python',
        script: `from lore_engine import world

def scan_for_contradictions(text_segment):
    print("Scanning narrative vector for logic faults...")
    # Mock analysis logic
    errors = 0
    if "laser swords" in text_segment and world.query("Technology") == "Medieval":
        print("CONFLICT: Anachronism detected.")
        errors += 1
    
    if errors == 0:
        print("Continuity Verified: 100% Consistent.")
    else:
        print(f"WARNING: {errors} logic breaks found.")

scan_for_contradictions("The knight drew his laser sword.")`
      },
      {
        name: 'arc_analyzer.py',
        language: 'python',
        script: `import math

def analyze_emotional_growth(character_logs):
    print(f"Analyzing arc for: {character_logs['name']}")
    
    growth_score = 0
    for event in character_logs['events']:
        impact = event['trauma'] * 1.5 + event['triumph']
        growth_score += math.log(impact + 1)
        print(f"Processed event: {event['name']} -> Impact: {impact:.1f}")
        
    return growth_score

protagonist = {
    'name': 'Kaelen',
    'events': [
        {'name': 'Village Raid', 'trauma': 8, 'triumph': 0},
        {'name': 'First Flight', 'trauma': 1, 'triumph': 10}
    ]
}

score = analyze_emotional_growth(protagonist)
print(f"Character Depth Score: {score:.2f}")`
      },
      {
        name: 'story.html',
        language: 'html',
        script: `<div id="editor-container">
    <div class="toolbar">
        <button>Bold</button>
        <button>Italic</button>
        <button class="save-btn">Save to Cloud</button>
    </div>
    <textarea placeholder="Start writing Chapter 1..."></textarea>
    <div class="word-count">Words: 0</div>
</div>
<style>
    textarea { width: 100%; height: 80vh; background: #222; color: #ddd; border: none; padding: 20px; font-size: 16px; }
    .toolbar { background: #333; padding: 10px; display: flex; gap: 10px; }
</style>`
      },
      {
        name: 'characters.html',
        language: 'html',
        script: `<div class="character-grid">
    <div class="char-card">
        <img src="avatar_kaelen.png" alt="Kaelen">
        <h3>Kaelen</h3>
        <p>Role: Protagonist</p>
        <p>Status: Active</p>
    </div>
    <div class="char-card villain">
        <h3>Lord Vrakos</h3>
        <p>Role: Antagonist</p>
        <p>Status: Unknown</p>
    </div>
</div>`
      },
      {
        name: 'timeline.html',
        language: 'html',
        script: `<div class="timeline">
    <div class="event" data-year="2020">
        <span class="date">2020</span>
        <span class="marker">●</span>
        <span class="label">The Awakening</span>
    </div>
    <div class="line"></div>
    <div class="event" data-year="2045">
        <span class="date">2045</span>
        <span class="marker">●</span>
        <span class="label">The First Colony</span>
    </div>
</div>`
      }
    ]
  }
];